import { memo } from 'react';
import { CharacterSprite } from '../../characters';
import { PIECE_VALUES, PIECE_STATUS, type GamePiece } from '@blind-kings/game-core';
import type { TeamPalette } from '../../player-colors';
import { getDefaultSkinIdForTeam, getPlayerSkinDef } from '../../player-colors';

export type PieceProps = {
  piece: GamePiece;
  pieceStatus?: string;
  skinId?: string;
  hidden?: boolean;
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
};

function teamFill(
  teamIndex: number | undefined,
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
) {
  if (teamIndex === 0 || teamIndex === 1) {
    const p = teamPalettes?.[teamIndex];
    if (p) return p.fill;
    return getPlayerSkinDef(getDefaultSkinIdForTeam(teamIndex)).fill;
  }
  return 'transparent';
}

function statusClass(pieceStatus: string) {
  if (pieceStatus === PIECE_STATUS.INACTIVE) return 'bk-piece-rail bk-piece-rail--waiting';
  if (pieceStatus === PIECE_STATUS.DEAD) return 'bk-piece-rail bk-piece-rail--dead';
  return 'bk-piece-rail bk-piece-rail--alive';
}

/** Still roster portrait. The playable board renders pieces through Pixi. */
function Piece({
  piece,
  pieceStatus = PIECE_STATUS.ACTIVE,
  skinId = 'classic',
  hidden = false,
  teamPalettes,
}: PieceProps) {
  const { value, teamIndex } = piece;

  if (hidden || value === PIECE_VALUES.Empty) {
    return <div className="w-full h-full" aria-hidden />;
  }

  const fill =
    pieceStatus === PIECE_STATUS.DEAD ? '#d1d5db' : teamFill(teamIndex, teamPalettes);
  const packId =
    (teamIndex === 0 || teamIndex === 1) && teamPalettes?.[teamIndex]?.spritePackId
      ? teamPalettes[teamIndex]!.spritePackId
      : skinId;

  return (
    <CharacterSprite
      pieceValue={value}
      skinId={packId}
      className={statusClass(pieceStatus)}
      svgProps={{ style: { fill, color: fill } }}
    />
  );
}

export default memo(Piece);
